import React, { useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { generateImagePath, ImagePreset } from '@vodafoneis/sjonvarpskjarni-js-lib';
import ContentInfo from '../../../components/ContentInfo/ContentInfo';
import { SimilarContent } from '../../../components/SimilarContent';
import { SeriesSceneContext, SeriesSceneContextProvider } from '../../../contexts/SeriesSceneContext';
import { SeriesEpisodes } from '../../../components/SeriesEpisodes';
import { Dropdown } from '../../../components/Dropdown';
import { ContentButtons } from '../../../components/ContentButtons/ContentButtons';
import { client } from '../../../api/ApolloClient';
import CONTENT_SIMPLE from '../../../graphql/ContentSimple.graphql';
import { MetaTags } from '../../../components/MetaTags';
import { processImageUrl } from '../../../components/ImageComponent';
import { ContentSsr } from '../../../@types/ContentSsr';
import { HeaderGap } from '../../../components/HeaderGap/HeaderGap.styles';
import { TempSpacerForOldDesign } from '../../../components/Divider';

const EpisodesWrapper = styled.div`
	padding-top: 20px;
	border-top: 1px solid #777;
	border-bottom: 1px solid #777;
`;

const DropdownWrapper = styled.div`
	margin-bottom: 20px;
`;

const SeriesSceneSeasons: React.FC = () => {
	const { t } = useTranslation();
	const { series, selectedSeason, setSelectedSeason } = useContext(SeriesSceneContext);

	const selected = useMemo(() => series?.seasons?.find((season) => season.number === selectedSeason), [selectedSeason, series?.seasons]);

	const seasonItems = useMemo(() => {
		if (series?.seasons?.filter((season) => season.number !== 0).length === 0) return null;

		return series?.seasons?.map((season) => ({ key: season.number, title: season.number > 0 ? t('Series.Season', { episodeSeason: season.number }) : t('Series.UnknownSeason') }));
	}, [series, t]);

	if (!seasonItems) return null;

	return (
		<DropdownWrapper>
			<Dropdown onChange={setSelectedSeason} value={selected?.number} items={seasonItems} />
		</DropdownWrapper>
	);
};

const SeriesSceneContent: React.FC = () => {
	const { series, episodes, nextEpisode } = useContext(SeriesSceneContext);

	return (
		<ContentInfo content={series}>
			<ContentButtons content={series} playable={nextEpisode} />
			<TempSpacerForOldDesign />
			{/* TempSpacerForOldDesign is needed to push down the content below the buttons, when move this page to the new design this can be removed */}
			{episodes?.length > 0 && (
				<EpisodesWrapper>
					<SeriesSceneSeasons />
					<SeriesEpisodes />
				</EpisodesWrapper>
			)}
			<SimilarContent content={series} />
		</ContentInfo>
	);
};

const SeriesScene: React.FC<{ contentSsr?: ContentSsr | null; resolvedUrl?: string }> = ({ contentSsr, resolvedUrl }) => {
	const router = useRouter();
	const { seriesId } = router.query;

	const content = useMemo(() => {
		if (!seriesId) return null;

		return (
			<div className={'series-scene'}>
				<SeriesSceneContextProvider seriesId={Number(seriesId)}>
					<SeriesSceneContent />
				</SeriesSceneContextProvider>
			</div>
		);
	}, [seriesId]);

	const socialImageUrl = useMemo(() => {
		if (!contentSsr?.promoImage?.hash && !contentSsr?.backdropImage?.hash) return null;

		return processImageUrl(generateImagePath({ hash: contentSsr?.promoImage?.hash ?? contentSsr?.backdropImage?.hash, presets: [ImagePreset.BACKDROP] }));
	}, [contentSsr]);

	if (typeof window !== 'undefined' && contentSsr?.id && contentSsr?.contentRating) {
		(window as any).__CONTENT_RATING_CACHE__ = (window as any).__CONTENT_RATING_CACHE__ || {};
		(window as any).__CONTENT_RATING_CACHE__[contentSsr.id] = contentSsr.contentRating;
	}

	return (
		<HeaderGap>
			<MetaTags title={contentSsr?.title} description={contentSsr?.description} socialImageUrl={socialImageUrl} type={'video.tv_show'} url={resolvedUrl} />
			{content}
		</HeaderGap>
	);
};

export default SeriesScene;

export const getServerSideProps = async ({ params, resolvedUrl }) => {
	const { seriesId } = params;

	const { data } = await client.query({
		query: CONTENT_SIMPLE,
		variables: {
			contentId: seriesId,
		},
	});

	return {
		props: {
			contentSsr: data.content,
			resolvedUrl,
		},
	};
};
